import { INoteCipher } from '@/contracts/modules/note/cipher/INoteCipher'
import { EnumNotePart } from '@/contracts/modules/note/enum/EnumNotePart'
import { EnumNoteError } from '@/contracts/modules/note/enum/EnumNoteError'
import { GCM_IV_BYTES, decryptGcm, encryptGcm } from '@/helpers/aesGcm'
import { base64UrlToBytes, bytesToBase64Url } from '@/helpers/base64url'

const KEY_ALGORITHM: AesKeyGenParams = { name: 'AES-GCM', length: 256 }
const KEY_USAGES: KeyUsage[] = ['encrypt', 'decrypt']
// тег AES-GCM — 16 байт: строка короче IV и тега не может быть шифротекстом
const TAG_BYTES = 16

/** AAD — часть заметки: шифротекст названия не расшифруется как содержимое */
function aad(part: EnumNotePart): Uint8Array {
  return new TextEncoder().encode(`note:${part}`)
}

/**
 * Каждое шифрование — новый случайный IV: одним ключом заметка шифруется много раз.
 * Строка — base64url от IV и шифротекста подряд.
 */
export class NoteCipher implements INoteCipher {
  createKey(): Promise<CryptoKey> {
    return crypto.subtle.generateKey(KEY_ALGORITHM, true, KEY_USAGES)
  }

  async encrypt(key: CryptoKey, part: EnumNotePart, text: string): Promise<string> {
    const iv = crypto.getRandomValues(new Uint8Array(GCM_IV_BYTES))
    const cipher = await encryptGcm(key, iv, aad(part), new TextEncoder().encode(text))
    const packed = new Uint8Array(iv.length + cipher.length)
    packed.set(iv)
    packed.set(cipher, iv.length)

    return bytesToBase64Url(packed)
  }

  async decrypt(key: CryptoKey, part: EnumNotePart, cipher: string): Promise<string> {
    try {
      const packed = base64UrlToBytes(cipher)
      if (packed.length < GCM_IV_BYTES + TAG_BYTES) {
        throw new Error(EnumNoteError.CANNOT_DECRYPT)
      }
      const bytes = await decryptGcm(key, packed.slice(0, GCM_IV_BYTES), aad(part), packed.slice(GCM_IV_BYTES))

      return new TextDecoder('utf-8', { fatal: true }).decode(bytes)
    } catch {
      throw new Error(EnumNoteError.CANNOT_DECRYPT)
    }
  }
}
